import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/authStore';
import {
  LayoutDashboard,
  User,
  Shield,
  Server,
  Trophy,
  Medal,
  Users,
  Calendar,
  Tv,
  FileText,
  Settings,
  UsersRound,
  LogOut,
  ChevronRight,
  Zap,
  Layers,
  Rss,
  Globe,
  UserCircle,
  Package,
  KeyRound,
  Database,
  Activity,
} from 'lucide-react';
import { clsx } from 'clsx';

interface NavItem {
  to: string;
  icon: React.ReactNode;
  label: string;
  adminOnly?: boolean;
}

interface SidebarProps {
  /** Drawer mobile aperto (irrilevante da md in su, dove la sidebar è sempre visibile) */
  isOpen: boolean;
  onClose: () => void;
}

export default function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { t } = useTranslation();
  const { user, logout } = useAuthStore();
  const isAdmin = user?.role === 'admin';

  const adminNavItems: NavItem[] = [
    { to: '/admin', icon: <LayoutDashboard size={18} />, label: t('nav.dashboard') },
    { to: '/admin/users', icon: <UsersRound size={18} />, label: t('nav.users') },
    { to: '/admin/diagnostics', icon: <Activity size={18} />, label: t('nav.diagnostics') },
    { to: '/admin/logs', icon: <FileText size={18} />, label: t('nav.logs') },
    { to: '/admin/packages', icon: <Package size={18} />, label: t('nav.packages') },
    { to: '/admin/wvds', icon: <KeyRound size={18} />, label: t('nav.wvds') },
    { to: '/admin/backup', icon: <Database size={18} />, label: t('nav.backup') },
    { to: '/admin/account', icon: <UserCircle size={18} />, label: t('nav.account') },
  ];

  const userNavItems: NavItem[] = [
    { to: '/dashboard', icon: <LayoutDashboard size={18} />, label: t('nav.dashboard') },
    { to: '/dazn-account', icon: <User size={18} />, label: t('nav.daznAccount') },
    { to: '/proxies', icon: <Shield size={18} />, label: t('nav.proxies') },
    { to: '/streaming-servers', icon: <Server size={18} />, label: t('nav.streamingServers') },
    { to: '/resolutions', icon: <Layers size={18} />, label: 'Resolutions' },
    { to: '/sports', icon: <Trophy size={18} />, label: t('nav.sports') },
    { to: '/competitions', icon: <Medal size={18} />, label: t('nav.competitions') },
    { to: '/teams', icon: <Users size={18} />, label: t('nav.teams') },
    { to: '/schedule', icon: <Calendar size={18} />, label: t('nav.schedule') },
    { to: '/epg', icon: <Rss size={18} />, label: t('nav.epg') },
    { to: '/linear-channels', icon: <Tv size={18} />, label: t('nav.linearChannels') },
    { to: '/cdn', icon: <Globe size={18} />, label: 'CDN' },
    { to: '/logs', icon: <FileText size={18} />, label: t('nav.logs') },
    { to: '/account', icon: <UserCircle size={18} />, label: t('nav.account') },
    { to: '/settings', icon: <Settings size={18} />, label: t('nav.settings') },
  ];

  const navItems = isAdmin ? adminNavItems : userNavItems;

  return (
    <aside
      className={clsx(
        'w-60 shrink-0 bg-white border-r border-dark-700 flex flex-col',
        // Mobile: drawer overlay che scivola da sinistra; da md in su: statica in flusso come oggi
        'fixed inset-y-0 left-0 z-40 transform transition-transform duration-200 ease-in-out',
        'md:static md:z-auto md:translate-x-0 md:transition-none',
        isOpen ? 'translate-x-0' : '-translate-x-full'
      )}
    >
      {/* Logo */}
      <div className="h-20 flex items-center gap-3 px-6 border-b border-dark-700">
        <div className="w-10 h-10 rounded-lg bg-primary-600 flex items-center justify-center shadow-sm">
          <Zap size={20} className="text-white" />
        </div>
        <div>
          <h1 className="text-lg font-bold text-dark-100 tracking-tight">DAZN Panel</h1>
          <p className="text-xs text-dark-500">Streaming Manager</p>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-3 scrollbar-hide">
        <div className="space-y-0.5">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/admin' || item.to === '/dashboard'}
              onClick={onClose}
              className={({ isActive }) =>
                clsx(
                  'group flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-200',
                  isActive
                    ? 'bg-[#eff6ff] text-primary-600'
                    : 'text-dark-400 hover:text-dark-200 hover:bg-dark-800'
                )
              }
            >
              <span className="flex-shrink-0">
                {item.icon}
              </span>
              <span className="flex-1">{item.label}</span>
              <ChevronRight
                size={14}
                className="opacity-0 -translate-x-2 transition-all duration-200 group-hover:opacity-40 group-hover:translate-x-0"
              />
            </NavLink>
          ))}
        </div>
      </nav>

      {/* User Info */}
      <div className="p-4 border-t border-dark-700">
        <div className="bg-dark-800 rounded-md p-3">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-10 h-10 rounded-md bg-primary-50 flex items-center justify-center border border-dark-700">
                <span className="text-sm font-bold text-primary-600">
                  {user?.username?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-success-500 rounded-full border-2 border-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-dark-100 truncate">
                {user?.username}
              </p>
              <p className="text-xs text-dark-500 capitalize flex items-center gap-1">
                <span className={clsx(
                  'w-1.5 h-1.5 rounded-full',
                  user?.role === 'admin' ? 'bg-warning-500' : 'bg-primary-500'
                )} />
                {user?.role}
              </p>
            </div>
            <button
              onClick={logout}
              className="p-2 rounded-md text-dark-500 hover:text-error-600 hover:bg-error-50 transition-all duration-200"
              title="Logout"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
}
